import React, { RefObject, useContext, useEffect, useId } from 'react';
import { useMachine, useSelector } from '@xstate/react';

import { DataAccessContext } from '@chargify/data-access';
import { useDeviceInfo } from '@chargify/device-provider';
import {
  Input,
  Box,
  Column,
  Row,
  FormField,
} from '@chargify/custom-ui-components';
import { SelectField } from '../../components';

import { addressMachine, addressSelectors } from '../../machines';
import { formatOptions } from './utils.ts';

import { defaultTranslations } from './translations';

import { AddressFields, FieldError } from '../../types';
import { AddressTranslations, Format } from './types';

type AddressProps = {
  /**
   * Address details
   */
  values: AddressFields;
  /**
   * Required fields configuration
   */
  requiredFields?: string[];
  /**
   * Address format
   */
  format?: Format;
  /**
   * Update address event handler
   */
  onChange: (name: keyof AddressFields, value: string) => void;
  /**
   * Fields errors
   */
  errors?: {
    [key in keyof AddressFields]?: FieldError;
  };
  /**
   * Validate fields
   */
  onValidateField?: (name: keyof AddressFields, value: string | number) => void;
  /**
   * Components translations
   */
  translations?: AddressTranslations;
  /**
   * Element to which the dropdwon should be closed on outside click
   */
  targetElementRef?: RefObject<HTMLElement>;
};

const Address = ({
  values,
  requiredFields = ['address', 'address2', 'city', 'country', 'zip', 'state'],
  errors = {},
  translations = defaultTranslations,
  format = 'us',
  onChange,
  onValidateField,
  targetElementRef,
}: AddressProps) => {
  const { dataAccess } = useContext(DataAccessContext);
  const { address, address2, city, state, country, zip } = values;
  const {
    address: addressTranslation,
    address2: address2Translation,
    city: cityTranslation,
    state: stateTranslation,
    country: countryTranslation,
    zip: zipTranslation,
    postalCode: postalCodeTranslation,
    region: regionTranslation,
    input: inputTranslations,
    error: { invalid: errorInvalidLabel, required: errorRequiredLabel },
  } = translations;

  const autoCompleteId = useId();

  const onValidate = (
    fieldName: keyof AddressFields,
    value: string | number
  ) => {
    onValidateField && onValidateField(fieldName, value);
  };

  const { device } = useDeviceInfo();
  const isOnMobileDevice = ['tablet', 'mobile'].includes(device);

  const [, sendToMachine, service] = useMachine(addressMachine, {
    services: {
      getCountries: () => dataAccess.getCountries(),
      getSubdivisions: (_ctx, event) => {
        if (event.type === 'FETCH_SUBDIVISION')
          return dataAccess.getCountrySubdivisions(event.country);
      },
    },
  });

  const countries = useSelector(service, addressSelectors.getCountries);
  const subdivisions = useSelector(service, addressSelectors.getSubdivisions);

  const handleErrorMessage = (error: FieldError) => {
    if (error === 'required') return errorRequiredLabel;
    if (error === 'invalid') return errorInvalidLabel;
    return null;
  };

  useEffect(() => {
    sendToMachine({ type: 'FETCH_COUNTRY' });
    if (country) {
      sendToMachine({ type: 'FETCH_SUBDIVISION', country });
    }
  }, []);

  return (
    <>
      <Box marginBottom={5}>
        <SelectField
          name="country"
          label={countryTranslation.label}
          placeholder={countryTranslation.placeholder}
          value={country}
          options={formatOptions(countries.items)}
          required={requiredFields.includes('country')}
          error={handleErrorMessage(errors.country)}
          searchLabel={inputTranslations.searchLabel}
          selectOptionLabel={inputTranslations.selectOptionLabel}
          onChange={(updatedCountry: string) => {
            onChange('country', updatedCountry);
            if (updatedCountry !== country) {
              onChange('state', '');
              sendToMachine({
                type: 'FETCH_SUBDIVISION',
                country: updatedCountry,
              });
            }
          }}
          onValidateField={(value) => onValidate('country', value)}
          isLoadingOptions={countries.isLoading}
          targetElementRef={targetElementRef}
        />
      </Box>
      <Box marginBottom={5}>
        <Row rowGap={16}>
          <Column span={isOnMobileDevice ? 24 : 12}>
            <FormField
              label={addressTranslation.label}
              name="address"
              hasError={!!errors.address}
              error={handleErrorMessage(errors.address)}
              required={requiredFields.includes('address')}
              fullWidth
            >
              <Input
                name="address"
                id="address"
                autoComplete={autoCompleteId}
                value={address}
                placeholder={addressTranslation.placeholder}
                fullWidth
                hasError={!!errors.address}
                onBlur={() => onValidate('address', address)}
                onChange={(e) => onChange('address', e.currentTarget.value)}
              />
            </FormField>
          </Column>
          <Column span={isOnMobileDevice ? 24 : 12}>
            <FormField
              label={address2Translation.label}
              name="address2"
              hasError={!!errors.address2}
              error={handleErrorMessage(errors.address2)}
              required={requiredFields.includes('address2')}
              fullWidth
            >
              <Input
                name="address2"
                id="address2"
                value={address2}
                autoComplete={autoCompleteId}
                placeholder={address2Translation.placeholder}
                fullWidth
                hasError={!!errors.address2}
                onBlur={() => onValidate('address2', address2)}
                onChange={(e) => onChange('address2', e.currentTarget.value)}
              />
            </FormField>
          </Column>
        </Row>
      </Box>
      {values.country &&
        (format === 'us' ? (
          <Row rowGap={16}>
            <Column span={isOnMobileDevice ? 24 : 12}>
              <FormField
                label={cityTranslation.label}
                name="city"
                hasError={!!errors.city}
                error={handleErrorMessage(errors.city)}
                required={requiredFields.includes('city')}
                fullWidth
              >
                <Input
                  name="city"
                  id="city"
                  value={city}
                  placeholder={cityTranslation.placeholder}
                  fullWidth
                  hasError={!!errors.city}
                  onBlur={() => onValidate('city', city)}
                  onChange={(e) => onChange('city', e.currentTarget.value)}
                />
              </FormField>
            </Column>
            <Column span={isOnMobileDevice ? 12 : 6}>
              <SelectField
                name="state"
                label={stateTranslation.label}
                placeholder={stateTranslation.placeholder}
                value={state}
                error={handleErrorMessage(errors.state)}
                searchLabel={inputTranslations.searchLabel}
                selectOptionLabel={inputTranslations.selectOptionLabel}
                options={formatOptions(subdivisions.items)}
                required={requiredFields.includes('state')}
                onChange={(state: string) => onChange('state', state)}
                onValidateField={(value) => onValidate('state', value)}
              />
            </Column>
            <Column span={isOnMobileDevice ? 12 : 6}>
              <FormField
                label={zipTranslation.label}
                name="zip"
                hasError={!!errors.zip}
                error={handleErrorMessage(errors.zip)}
                required={requiredFields.includes('zip')}
                fullWidth
              >
                <Input
                  name="zip"
                  id="zip"
                  value={zip}
                  placeholder={zipTranslation.placeholder}
                  fullWidth
                  hasError={!!errors.zip}
                  onBlur={() => onValidate('zip', zip)}
                  onChange={(e) => onChange('zip', e.currentTarget.value)}
                />
              </FormField>
            </Column>
          </Row>
        ) : (
          <Row rowGap={16}>
            <Column span={isOnMobileDevice ? 12 : 8}>
              <FormField
                label={postalCodeTranslation.label}
                name="zip"
                hasError={!!errors.zip}
                error={handleErrorMessage(errors.zip)}
                required={requiredFields.includes('zip')}
                fullWidth
              >
                <Input
                  name="zip"
                  id="zip"
                  value={zip}
                  placeholder={postalCodeTranslation.placeholder}
                  fullWidth
                  hasError={!!errors.zip}
                  onBlur={() => onValidate('zip', zip)}
                  onChange={(e) => onChange('zip', e.currentTarget.value)}
                />
              </FormField>
            </Column>
            <Column span={isOnMobileDevice ? 12 : 8}>
              <FormField
                label={cityTranslation.label}
                name="city"
                hasError={!!errors.city}
                error={handleErrorMessage(errors.city)}
                required={requiredFields.includes('city')}
                fullWidth
              >
                <Input
                  name="city"
                  id="city"
                  value={city}
                  placeholder={cityTranslation.placeholder}
                  fullWidth
                  hasError={!!errors.city}
                  onBlur={() => onValidate('city', city)}
                  onChange={(e) => onChange('city', e.currentTarget.value)}
                />
              </FormField>
            </Column>
            <Column span={isOnMobileDevice ? 24 : 8}>
              <SelectField
                name="state"
                searchLabel={inputTranslations.searchLabel}
                selectOptionLabel={inputTranslations.selectOptionLabel}
                label={regionTranslation.label}
                placeholder={regionTranslation.placeholder}
                error={handleErrorMessage(errors.state)}
                value={state}
                options={formatOptions(subdivisions.items)}
                required={requiredFields.includes('state')}
                onChange={(state: string) => onChange('state', state)}
                onValidateField={(value) => onValidate('state', value)}
                isLoadingOptions={subdivisions.isLoading}
              />
            </Column>
          </Row>
        ))}
    </>
  );
};

export default Address;
